import React, { useState, useEffect } from 'react';
import './MembersPage.css';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { businessService } from '../../../shared/services/firebaseService';

const MembersPage = () => {
  const { user } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const membersPerPage = 10;

  useEffect(() => {
    const fetchMembers = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const membersData = await businessService.getRewardMembers(user.uid, 1000);
        setMembers(membersData);
      } catch (error) {
        console.error('Error fetching members:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, [user]);

  const formatDate = (dateValue) => {
    if (!dateValue) return 'Never';
    
    // Handle Firestore timestamp
    let date;
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      date = dateValue.toDate();
    } else if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    } else {
      date = dateValue;
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Pagination calculations
  const totalPages = Math.ceil(members.length / membersPerPage);
  const startIndex = (currentPage - 1) * membersPerPage;
  const endIndex = startIndex + membersPerPage;
  const currentMembers = members.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Calculate stats
  const totalMembers = members.length;
  const activeMembers = members.filter(m => (m.lifetimeRedemptions || 0) > 0).length;
  const thisMonth = new Date();
  thisMonth.setDate(1);
  const newThisMonth = members.filter(m => {
    if (!m.dateJoined) return false;
    const joinDate = m.dateJoined.toDate ? m.dateJoined.toDate() : new Date(m.dateJoined);
    return joinDate >= thisMonth;
  }).length;

  if (loading) {
    return (
      <div className="members-page loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="members-page">
      <div className="page-header">
        <h1>Members</h1>
        <p>Manage and view all loyalty program members</p>
      </div>

      <div className="members-action-buttons">
        <button className="members-action-btn">Adjust Points</button>
        <button className="members-action-btn">Create Member</button>
      </div>

      <div className="members-section">
        <div className="members-container">
          <div className="members-stats">
            <div className="stat-item">
              <span className="stat-number">{totalMembers}</span>
              <span className="stat-label">Total Members</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{activeMembers}</span>
              <span className="stat-label">Active Members</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{newThisMonth}</span>
              <span className="stat-label">New This Month</span>
            </div>
          </div>

          <div className="table-wrapper">
            <table className="members-table">
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Email</th>
                  <th>Date Joined</th>
                  <th>Total Taps</th>
                  <th>Total Redemptions</th>
                  <th>Last Tap</th>
                  <th>Last Redemption</th>
                  <th>Current Points</th>
                </tr>
              </thead>
              <tbody>
                {currentMembers.length > 0 ? (
                  currentMembers.map((member, index) => (
                    <tr key={member.id || index}>
                      <td>{member.id || `Member ${index + 1}`}</td>
                      <td>{member.email}</td>
                      <td>{formatDate(member.dateJoined)}</td>
                      <td>{member.lifetimeTaps || 0}</td>
                      <td>{member.lifetimeRedemptions || 0}</td>
                      <td>{formatDate(member.lastTap)}</td>
                      <td>{formatDate(member.lastRedemption)}</td>
                      <td>{member.currentPoints || 0}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                      No members found. Members will appear here when customers start using your loyalty program.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          <div className="pagination">
            <div className="pagination-info">
              Showing {members.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, members.length)} of {members.length} members
            </div>
            <div className="pagination-controls">
              <button 
                className="pagination-btn"
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              
              <div className="page-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    className={`page-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => goToPage(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>
              
              <button 
                className="pagination-btn"
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembersPage;
